package com.bulut.mecitogullari.ui.screens.projectdetail

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.export.ProjectReportHtml
import com.bulut.mecitogullari.core.export.safePdfName
import com.bulut.mecitogullari.core.importer.ExcelExport
import com.bulut.mecitogullari.core.platform.isExcelSupported
import com.bulut.mecitogullari.core.platform.isPrintSupported
import com.bulut.mecitogullari.core.platform.printHtml
import com.bulut.mecitogullari.core.platform.saveExcelFile
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.ConstructionStages
import com.bulut.mecitogullari.data.model.UnitStatus
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.components.SectionTitle
import com.bulut.mecitogullari.ui.components.TransactionRow
import com.bulut.mecitogullari.ui.excel.ExcelImportDialogs
import com.bulut.mecitogullari.ui.theme.AppColors
import com.bulut.mecitogullari.ui.theme.parseHexColor
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProjectDetailScreen(
    projectId: String,
    onTransactionClick: (transactionId: String) -> Unit,
    onBack: () -> Unit,
    viewModel: ProjectDetailViewModel = koinViewModel { parametersOf(projectId) },
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val projectDeleted by viewModel.projectDeleted.collectAsStateWithLifecycle()
    val excelImportState by viewModel.excelImportState.collectAsStateWithLifecycle()
    var selectedUnit by remember { mutableStateOf<ApartmentUnit?>(null) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    val strings = LocalStrings.current

    LaunchedEffect(projectDeleted) {
        if (projectDeleted) onBack()
    }

    ExcelImportDialogs(
        state = excelImportState,
        onConfirm = viewModel::confirmExcelImport,
        onDismiss = viewModel::dismissExcelImport,
    )

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(uiState.project?.name ?: strings.project) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    if (isExcelSupported && uiState.project != null) {
                        var excelMenuOpen by remember { mutableStateOf(false) }
                        Box {
                            IconButton(onClick = { excelMenuOpen = true }) {
                                Icon(
                                    Icons.Default.List,
                                    contentDescription = strings.excelMenu,
                                    tint = AppColors.TextSecondary,
                                )
                            }
                            DropdownMenu(
                                expanded = excelMenuOpen,
                                onDismissRequest = { excelMenuOpen = false },
                            ) {
                                DropdownMenuItem(
                                    text = { Text(strings.exportExcel) },
                                    enabled = uiState.allTransactions.isNotEmpty(),
                                    onClick = {
                                        excelMenuOpen = false
                                        uiState.project?.let { project ->
                                            saveExcelFile(
                                                fileName = ExcelExport.fileNameForProject(project.name),
                                                rows = ExcelExport.rows(
                                                    transactions = uiState.allTransactions,
                                                    projectNames = mapOf(project.id to project.name),
                                                ),
                                            )
                                        }
                                    },
                                )
                                DropdownMenuItem(
                                    text = { Text(strings.importExcelToProject) },
                                    onClick = {
                                        excelMenuOpen = false
                                        viewModel.startExcelImport()
                                    },
                                )
                            }
                        }
                    }
                    if (isPrintSupported && uiState.project != null) {
                        IconButton(
                            onClick = {
                                uiState.project?.let { project ->
                                    printHtml(
                                        html = ProjectReportHtml.build(
                                            project = project,
                                            units = uiState.blocks.flatMap { it.units },
                                            transactions = uiState.allTransactions,
                                            craftsmanNames = uiState.craftsmanNames,
                                        ),
                                        fileName = "proje-${safePdfName(project.name)}.pdf",
                                    )
                                }
                            },
                        ) {
                            Icon(Icons.Default.Share, contentDescription = strings.downloadPdf)
                        }
                    }
                    IconButton(onClick = { showDeleteConfirm = true }) {
                        Icon(Icons.Default.Delete, contentDescription = strings.deleteProjectAction, tint = AppColors.Expense)
                    }
                },
            )
        },
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding),
            contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            uiState.project?.let { project ->
                item {
                    Card(
                        shape = RoundedCornerShape(18.dp),
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(8.dp)
                                .background(parseHexColor(project.coverColorHex)),
                        )
                        Column(modifier = Modifier.padding(16.dp)) {
                            if (project.location.isNotBlank()) {
                                InfoLine(strings.locationLabel, project.location)
                            }
                            project.startDateEpochMillis?.let { InfoLine(strings.startLabel, formatDate(it)) }
                            project.expectedEndEpochMillis?.let { InfoLine(strings.estimatedEndLabel, formatDate(it)) }
                            // Multi-block projects list each block's own layout.
                            val blocks = project.effectiveBlocks
                            if (blocks.size > 1) {
                                blocks.forEach { block ->
                                    InfoLine(
                                        "${block.name} ${strings.blockWord}".trim(),
                                        strings.blockLayoutSummary.fillTemplate(
                                            "floors" to block.floors.toString(),
                                            "perFloor" to block.apartmentsPerFloor.toString(),
                                        ),
                                    )
                                }
                            } else {
                                InfoLine(
                                    strings.buildingLabel,
                                    strings.buildingValue.fillTemplate(
                                        "floors" to blocks.first().floors.toString(),
                                        "perFloor" to blocks.first().apartmentsPerFloor.toString(),
                                    ),
                                )
                            }
                            if (project.notes.isNotBlank()) {
                                Text(
                                    text = project.notes,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = AppColors.TextSecondary,
                                    modifier = Modifier.padding(top = 8.dp),
                                )
                            }
                        }
                    }
                }
            }

            item {
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    DetailStat("Gider", formatAmount(uiState.totalExpense), AppColors.Expense, Modifier.weight(1f))
                    DetailStat("Gelir", formatAmount(uiState.totalIncome), AppColors.Income, Modifier.weight(1f))
                    DetailStat(
                        strings.statSales,
                        "${uiState.soldCount}/${uiState.totalUnits}",
                        AppColors.TextPrimary,
                        Modifier.weight(1f),
                        subtitle = formatAmount(uiState.salesTotal),
                    )
                }
            }

            uiState.project?.let { project ->
                item { SectionTitle(strings.constructionStatus) }
                item {
                    ConstructionProgressCard(
                        completedStages = project.completedStages,
                        progressPercent = project.progressPercent,
                        onToggleStage = viewModel::toggleStage,
                    )
                }
            }

            item { SectionTitle(strings.buildingStatus) }
            item { StatusLegend() }

            if (uiState.blocks.isEmpty()) {
                item { EmptyState(strings.noUnitsDefined) }
            } else {
                uiState.blocks.forEach { block ->
                    // A single unnamed block keeps the old, header-less layout.
                    if (uiState.blocks.size > 1 || block.name.isNotBlank()) {
                        item(key = "block-${block.name}") {
                            BlockHeader(block = block)
                        }
                    }
                    items(block.floors, key = { "${block.name}-${it.first}" }) { (floor, units) ->
                        FloorRow(
                            floor = floor,
                            units = units,
                            onUnitClick = { selectedUnit = it },
                        )
                    }
                }
            }

            val soldUnits = uiState.blocks.flatMap { it.units }.filter { it.status == UnitStatus.SOLD }
            if (soldUnits.isNotEmpty()) {
                item { SectionTitle(strings.buyersSection) }
                items(soldUnits, key = { "buyer-${it.id}" }) { unit ->
                    BuyerRow(unit = unit, onClick = { selectedUnit = unit })
                }
            }

            item { SectionTitle(strings.projectTransactionsSection) }
            if (uiState.recentTransactions.isEmpty()) {
                item { EmptyState(strings.noProjectTransactions) }
            } else {
                items(uiState.recentTransactions, key = { it.id }) { transaction ->
                    TransactionRow(
                        transaction = transaction,
                        onClick = { onTransactionClick(transaction.id) },
                    )
                }
            }
        }
    }

    selectedUnit?.let { unit ->
        UnitDetailDialog(
            unit = unit,
            barterCraftsmanName = unit.barterCraftsmanId?.let { uiState.craftsmanNames[it] },
            onSave = { updated ->
                viewModel.updateUnit(updated)
                selectedUnit = null
            },
            onDismiss = { selectedUnit = null },
        )
    }

    if (showDeleteConfirm) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            title = { Text(strings.deleteProjectTitle) },
            text = {
                Text(
                    strings.deleteProjectMessage
                        .fillTemplate("name" to uiState.project?.name.orEmpty())
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteConfirm = false
                        viewModel.deleteProject()
                    },
                ) {
                    Text(strings.delete, color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirm = false }) { Text(strings.cancel) }
            },
        )
    }
}

/** "A Blok — 3/24 satıldı" strip above each block's floor grid. */
@Composable
private fun BlockHeader(block: BlockGrid) {
    val strings = LocalStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 6.dp)
            .background(AppColors.SurfaceMuted, RoundedCornerShape(10.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = "${block.name} ${strings.blockWord}".trim(),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
        )
        Text(
            text = strings.blockSummary.fillTemplate(
                "sold" to block.soldCount.toString(),
                "total" to block.units.size.toString(),
            ),
            style = MaterialTheme.typography.labelMedium,
            color = AppColors.TextSecondary,
        )
    }
}

@Composable
private fun InfoLine(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(label, style = MaterialTheme.typography.bodySmall, color = AppColors.TextSecondary)
        Text(value, style = MaterialTheme.typography.bodySmall, color = AppColors.TextPrimary)
    }
}

@Composable
private fun DetailStat(
    title: String,
    value: String,
    valueColor: Color,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Text(title, style = MaterialTheme.typography.labelMedium, color = AppColors.TextSecondary, maxLines = 1)
            Text(
                text = value,
                style = MaterialTheme.typography.titleSmall,
                color = valueColor,
                maxLines = 1,
                modifier = Modifier.padding(top = 4.dp),
            )
            if (subtitle != null) {
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.labelSmall,
                    color = AppColors.TextSecondary,
                    maxLines = 1,
                )
            }
        }
    }
}

/**
 * Collapsed: just the progress bar. Expanded: tappable stage checklist
 * (tap to mark done, tap again to undo; progress = weight sum).
 */
@Composable
private fun ConstructionProgressCard(
    completedStages: List<String>,
    progressPercent: Int,
    onToggleStage: (String) -> Unit,
) {
    val strings = LocalStrings.current
    var expanded by remember { mutableStateOf(false) }

    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier
                .clickable { expanded = !expanded }
                .padding(16.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = if (progressPercent >= 100) strings.completedParty else strings.progressLabel,
                    style = MaterialTheme.typography.labelLarge,
                    color = if (progressPercent >= 100) AppColors.Income else AppColors.TextSecondary,
                )
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = "%$progressPercent",
                        style = MaterialTheme.typography.titleMedium,
                        color = if (progressPercent >= 100) AppColors.Income else AppColors.NavyDeep,
                    )
                    Icon(
                        if (expanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
                        contentDescription = if (expanded) "Daralt" else strings.showStages,
                        tint = AppColors.TextSecondary,
                        modifier = Modifier.padding(start = 4.dp),
                    )
                }
            }
            LinearProgressIndicator(
                progress = { progressPercent / 100f },
                color = if (progressPercent >= 100) AppColors.Income else AppColors.Navy,
                trackColor = AppColors.Outline,
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp).height(8.dp),
            )
        }
        AnimatedVisibility(visible = expanded) {
            Column(modifier = Modifier.padding(start = 16.dp, end = 16.dp, bottom = 12.dp)) {
                ConstructionStagesList(completedStages, onToggleStage)
            }
        }
    }
}

@Composable
private fun ConstructionStagesList(
    completedStages: List<String>,
    onToggleStage: (String) -> Unit,
) {
    Column {
        ConstructionStages.all.forEach { stage ->
                val done = stage.id in completedStages
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onToggleStage(stage.id) }
                        .padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        modifier = Modifier
                            .size(24.dp)
                            .background(
                                color = if (done) AppColors.Income else Color.Transparent,
                                shape = CircleShape,
                            )
                            .border(
                                width = if (done) 0.dp else 2.dp,
                                color = if (done) Color.Transparent else AppColors.Outline,
                                shape = CircleShape,
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (done) {
                            Icon(
                                Icons.Default.Check,
                                contentDescription = null,
                                tint = Color.White,
                                modifier = Modifier.size(14.dp),
                            )
                        }
                    }
                    Text(
                        text = stage.label,
                        style = MaterialTheme.typography.bodyMedium,
                        color = if (done) AppColors.TextPrimary else AppColors.TextSecondary,
                        fontWeight = if (done) FontWeight.Medium else FontWeight.Normal,
                        modifier = Modifier.weight(1f).padding(start = 12.dp),
                    )
                    Text(
                        text = "%${stage.weight}",
                        style = MaterialTheme.typography.labelMedium,
                        color = AppColors.TextSecondary,
                    )
                }
            }
        }
    }

/** One sold unit with its buyer and paid/remaining amounts. */
@Composable
private fun BuyerRow(
    unit: ApartmentUnit,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .background(AppColors.Income.copy(alpha = 0.14f), RoundedCornerShape(12.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = unit.label,
                    style = MaterialTheme.typography.labelLarge,
                    color = AppColors.Income,
                    fontWeight = FontWeight.Bold,
                )
            }
            Column(modifier = Modifier.weight(1f).padding(horizontal = 12.dp)) {
                Text(
                    text = unit.buyerName.ifBlank { strings.noBuyerGiven },
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis,
                )
                Text(
                    text = strings.saleSummary.fillTemplate(
                    "unit" to unitDisplayLabel(unit.block, unit.number),
                    "amount" to formatAmount(unit.salePrice ?: 0.0),
                ),
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )
            }
            Column(horizontalAlignment = androidx.compose.ui.Alignment.End) {
                if (unit.barterCraftsmanId != null) {
                    // Barter: the account lives on the craftsman page.
                    Text(
                        text = strings.barterShort,
                        style = MaterialTheme.typography.labelMedium,
                        color = AppColors.Reserved,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(
                        text = "Takas",
                        style = MaterialTheme.typography.labelSmall,
                        color = AppColors.TextSecondary,
                    )
                } else {
                    Text(
                        text = strings.paidAmount.fillTemplate("amount" to formatAmount(unit.paidTotal)),
                        style = MaterialTheme.typography.labelMedium,
                        color = AppColors.Income,
                    )
                    if (unit.remaining > 0) {
                        Text(
                            text = "Kalan ${formatAmount(unit.remaining)}",
                            style = MaterialTheme.typography.labelMedium,
                            color = AppColors.Expense,
                        )
                    } else {
                        Text(
                            text = strings.completedCheck,
                            style = MaterialTheme.typography.labelMedium,
                            color = AppColors.TextSecondary,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun StatusLegend() {
    val strings = LocalStrings.current
    Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
        LegendItem(AppColors.Income, strings.statusSold)
        LegendItem(AppColors.Reserved, strings.statusReserved)
        LegendItem(AppColors.Outline, strings.statusAvailable)
    }
}

@Composable
private fun LegendItem(color: Color, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(modifier = Modifier.size(12.dp).background(color, CircleShape))
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = AppColors.TextSecondary,
            modifier = Modifier.padding(start = 6.dp),
        )
    }
}

/** One floor of the building: "Kat N" + a row of clickable apartment cells. */
@Composable
private fun FloorRow(
    floor: Int,
    units: List<ApartmentUnit>,
    onUnitClick: (ApartmentUnit) -> Unit,
) {
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Text(
                text = "Kat $floor",
                style = MaterialTheme.typography.labelMedium,
                color = AppColors.TextSecondary,
            )
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
            ) {
                units.forEach { unit ->
                    UnitCell(
                        unit = unit,
                        modifier = Modifier.weight(1f),
                        onClick = { onUnitClick(unit) },
                    )
                }
            }
        }
    }
}

@Composable
private fun UnitCell(
    unit: ApartmentUnit,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val (background, border, textColor) = when (unit.status) {
        UnitStatus.SOLD -> Triple(AppColors.Income, Color.Transparent, Color.White)
        UnitStatus.RESERVED -> Triple(AppColors.ReservedContainer, AppColors.Reserved, AppColors.Reserved)
        UnitStatus.AVAILABLE -> Triple(AppColors.SurfaceMuted, AppColors.Outline, AppColors.TextSecondary)
    }
    Box(
        modifier = modifier
            .height(44.dp)
            .background(background, RoundedCornerShape(10.dp))
            .border(1.dp, border, RoundedCornerShape(10.dp))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = unit.label,
            color = textColor,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold,
            textAlign = TextAlign.Center,
        )
    }
}
